import React from 'react';
import { HubMarketingNav } from '../../components/ui/HubHeader';
import Seo from '../../components/Seo';
import { Link } from 'react-router-dom';

const SheetsSupportPage = () => {
  return (
    <>
      <Seo
        title="Neevii Sheets Sidebar Support | Neevii"
        description="Support and troubleshooting for Neevii AI Assistant in Google Sheets."
      />
      <div className="min-h-screen">
        <HubMarketingNav preset="sheets" />
        <main className="pt-10 pb-16 px-6 md:pt-12 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-8">
            <header className="space-y-3">
              <h1 className="text-4xl font-bold text-foreground">
                Neevii Sheets Sidebar Support
              </h1>
              <p className="text-muted-foreground">
                Help for install, sign-in, and billing issues related to the
                Neevii AI Assistant Google Sheets sidebar.
              </p>
            </header>

            <section className="rounded-2xl border border-border bg-card/30 p-6">
              <h2 className="text-2xl font-semibold mb-3">Contact support</h2>
              <p className="text-muted-foreground mb-2">
                Email: <a className="text-brand hover:underline" href="mailto:support@neevii.com">support@neevii.com</a>
              </p>
              <p className="text-muted-foreground">
                Include your Google account email, marketplace order/subscription
                details, and a short description of the issue.
              </p>
            </section>

            <section className="rounded-2xl border border-[#065aaf]/30 bg-[#065aaf]/5 p-6">
              <h2 className="text-2xl font-semibold mb-3">How to open the sidebar</h2>
              <p className="text-muted-foreground mb-4">
                Marketplace installs use the Sheets <strong>Extensions</strong> menu. If you only
                see a separate top-level <strong>Neevii AI</strong> item, you are on an old
                bound-script copy — reinstall from the{' '}
                <Link className="text-brand hover:underline" to="/sheets">
                  Sheets page
                </Link>{' '}
                Marketplace link instead.
              </p>
              <ol className="list-decimal pl-6 space-y-2 text-muted-foreground">
                <li>
                  <strong>Extensions</strong> → <strong>Neevii Smart Expenses</strong> →{' '}
                  <strong>Open/Reopen Neevii Sidebar</strong>
                </li>
                <li>
                  <strong>Continue with Google</strong> in the sidebar, then{' '}
                  <strong>Create expense template</strong>
                </li>
              </ol>
            </section>

            <section className="rounded-2xl border border-border bg-card/30 p-6">
              <h2 className="text-2xl font-semibold mb-4">Common issues</h2>
              <div className="space-y-4 text-muted-foreground">
                <div>
                  <h3 className="font-semibold text-foreground">I do not see Neevii under Extensions</h3>
                  <p>
                    Confirm the add-on is installed from Marketplace (not pasted Apps Script code).
                    Refresh the sheet, or open a new spreadsheet and check Extensions again.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Install button does not open marketplace</h3>
                  <p>Ensure pop-ups are enabled and retry from the `/sheets` page using the latest browser version.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">OAuth consent keeps repeating</h3>
                  <p>Sign out of duplicate Google sessions, then reinstall. Domain admins may need to approve scopes for workspace users.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Usage limit reached</h3>
                  <p>The sidebar enforces monthly usage tiers. Upgrade from the in-sidebar prompt or contact support for account review.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Billing or refund request</h3>
                  <p>Reference your subscription email and review refund terms in <a className="text-brand hover:underline" href="/terms">Terms of Service</a>.</p>
                </div>
              </div>
            </section>

            <section className="rounded-2xl border border-brand/30 bg-brand/5 p-6">
              <h2 className="text-2xl font-semibold mb-3">Setup guide</h2>
              <p className="text-muted-foreground mb-4">
                Open the visual onboarding guide for the sidebar, sheet area, and
                setup flow references.
              </p>
              <Link
                to="/support-sheets/guide"
                className="inline-flex items-center justify-center rounded-lg bg-[#065aaf] px-5 py-3 text-sm font-semibold uppercase tracking-wide text-white hover:bg-[#065aaf]/90"
              >
                Open guide
              </Link>
            </section>
          </div>
        </main>
      </div>
    </>
  );
};

export default SheetsSupportPage;
